import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {useRolesPermissions} from 'src/admin-rights-service/modules/roles-page/useRolesPermissions';
import {BasicPage} from 'src/components/basic-page';
import {
    MainColumnCell,
    MainColumnHeader,
    getPaginatedRowNumber,
} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {NO_RESULTS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {NotificationAdminProfileHeader} from 'src/modules/internal-notification/notification-list/headers/admin-profile/notification-admin-profile.header';
import {NotificationEndDateHeader} from 'src/modules/internal-notification/notification-list/headers/date/notification-end-date.header';
import {NotificationStartDateHeader} from 'src/modules/internal-notification/notification-list/headers/date/notification-start-date.header';
import {ClearFilters} from 'src/modules/internal-notification/notification-list/headers/quick-filter/clear-filters';
import {DefaultFilters} from 'src/modules/internal-notification/notification-list/headers/quick-filter/default-filters';
import {NotificationTitleHeader} from 'src/modules/internal-notification/notification-list/headers/title/notification-title.header';
import {NotificationTopicHeader} from 'src/modules/internal-notification/notification-list/headers/topic/notification-topic.header';
import {notificationsListStateActions, notificationsListStateSelector} from 'src/modules/internal-notification/notification-list/slices/notification-search-criteria.slice';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Cell, DataTable, TableHeader} from '@pb/kit-component-data-table-v2';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {LocaleContext} from '@pb/kit-context-locale';
import {pluralize} from '@pb/kit-formatters';

import {COLUMNS, COLUMNS_DATA, HEADERS} from './constants';
import {useDefaultFilters} from './hooks/useDefaultFilters';
import {useSearchNotification} from './hooks/useSearchNotification';
import {NotificationPagination} from './notification-pagination';
import {REMOVE_NOTIFICATION_MODAL_WIDTH, RemoveNotification} from './remove-notification';

import type {CellOptions, Column, HeaderCellOptions, RowsParams} from '@pb/kit-component-data-table-v2';
import type {AccessRight} from 'src/hooks/access-rights/constants';

export const InternalNotificationList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {locale} = useContext(LocaleContext);
    const {setSuccessMessage} = useContext(MessageContext);
    const defaultFilters = useDefaultFilters();
    const state = useSelector(notificationsListStateSelector);
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const [notificationIdForRemove, setNotificationIdForRemove] = useState<string | undefined>(undefined);

    const {
        isLoading: roleLoading,
        loadingError: roleLoadingError,
        data: roles,
        loadData: loadRoles,
    } = useRolesPermissions();

    const {
        isLoading: isNotificationsLoading,
        loadingError: notificationsLoadingError,
        data,
        loadData: loadNotifications,
    } = useSearchNotification();

    useEffect(() => {
        dispatch(notificationsListStateActions.setFilters(defaultFilters));
        loadRoles();

        return () => {
            dispatch(notificationsListStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev: prev.state, current: current.state})) {
            current.loadNotifications();
        }
    }, {state, loadNotifications});

    const refreshList = useCallback(() => {
        dispatch(notificationsListStateActions.setCurrentPage(1));
        loadNotifications();
    }, [loadNotifications, dispatch]);

    const navigateToCreateNotificationPage = useCallback(() => {
        navigate('/internal-notification/builder');
    }, [navigate]);

    const navigateToEditNotificationPage = useCallback((notificationId: string) => {
        navigate(`/internal-notification/${notificationId}`);
    }, [navigate]);

    const onRemoveNotification = useCallback(() => {
        setSuccessMessage('Notification has been removed');
        setNotificationIdForRemove(undefined);
        refreshList();
    }, [refreshList, setSuccessMessage]);

    const renderedRows = useMemo(() => (data?.notifications
        .map(notification => ({
            id: notification.notificationId,
            columnValues: {
                ...notification,
                [COLUMNS.ADMIN_PROFILES]: notification.adminProfileIds.length > 0
                    ? notification.adminProfileIds.map((profileId: number) => {
                            const role = roles?.find(item => item.profileId === profileId);
                            return role?.profileName || 'N/A';
                        }).join('; ')
                    : 'All profiles',
            },
        }))
    ), [data, roles]);

    const maxRowNumber = getPaginatedRowNumber({...state.pagination, index: renderedRows?.length});

    return (
        <ServerResponseHandler
            serverError={notificationsLoadingError || roleLoadingError}
            accessRight={ACCESS_RIGHTS.INTERNAL_NOTIFICATION_BUILDER as AccessRight}
            sectionName="Internal Notification Builder">
            <BasicPage
                title="Admin Notification Builder"
                subtitle={data?.total
                    ? pluralize(`${Integer.format(data.total, locale)} Notification[s]`, data.total)
                    : undefined}
                buttonsBlock={(
                    <Group>
                        <DefaultFilters/>
                        <ClearFilters/>
                        <Button variant="Type.PRIMARY" onClick={navigateToCreateNotificationPage}>
                            Add Notification
                        </Button>
                    </Group>
                )}>
                <BoxLoading loading={isNotificationsLoading || roleLoading}>
                    <DataTable
                        data={{
                            rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                                ...column,
                                width: getColumnWidthByColumnId(column.columnId),
                            })),
                        }}
                        getHeaderTopOffset={() => 112}
                        onStopResize={onStopResize}
                        headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                            switch (column.columnId) {
                                case COLUMNS.TITLE:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <MainColumnHeader
                                                selection={<WidthItem width={50}/>}
                                                maxRowNumber={maxRowNumber}>
                                                <NotificationTitleHeader/>
                                            </MainColumnHeader>
                                        </Cell>
                                    );
                                case COLUMNS.TOPIC:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <NotificationTopicHeader/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                case COLUMNS.ADMIN_PROFILES:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <NotificationAdminProfileHeader roles={roles}/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                case COLUMNS.START_DATE:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <NotificationStartDateHeader/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                case COLUMNS.END_DATE:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <NotificationEndDateHeader/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                {HEADERS[column.columnId]}
                                            </TableHeader>
                                        </Cell>
                                    );
                            }
                        }}
                        renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                            const {rowIndex, column: {columnId}} = rowsParams;
                            const value = rowsParams.row.columnValues[columnId] as string | number;
                            const rowId = rowsParams.row.id + '';
                            const column = rowsParams.column;
                            const cellKey = `${rowId}_${columnId}`;
                            const rowNumber = getPaginatedRowNumber({...state.pagination, index: rowIndex + 1});

                            switch (columnId) {
                                case COLUMNS.TITLE:
                                    return (
                                        <MainColumnCell
                                            key={rowIndex + columnId + value}
                                            {...{
                                                selection: (
                                                    <div

                                                        className="data-table_hidden-tools data-table_hidden-tools_visible">
                                                        <Tooltip
                                                            anchor={({anchorRef, getAnchorProps}) => (
                                                                <BasicIconButton
                                                                    ref={anchorRef}
                                                                    {...getAnchorProps()}
                                                                    onClick={() => setNotificationIdForRemove(rowId)}
                                                                    iconProps={{type: Icons.TRASH_LIGHT}}
                                                                />
                                                            )}>
                                                            Remove notification
                                                        </Tooltip>
                                                    </div>
                                                ),
                                                maxRowNumber,
                                                rowNumber,
                                                cellOptions: {...cellOptions, column},
                                            }}>
                                            <a onClick={() => navigateToEditNotificationPage(rowId + '')}>
                                                <EllipsisTextHint title={String(value)}>
                                                    {value}
                                                </EllipsisTextHint>
                                            </a>
                                        </MainColumnCell>
                                    );
                                default:
                                    const displayValue = value !== -1 ? String(value) : 'N/A';
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className="search-result-cell">
                                            <EllipsisTextHint title={displayValue}>
                                                {displayValue}
                                            </EllipsisTextHint>
                                        </Cell>
                                    );
                            }

                        }}
                        paginationStripe={<NotificationPagination totalCount={data?.total || 0}/>}
                        showNoDataMsg={!data?.notifications || data?.total === 0}
                        tableMessage={NO_RESULTS_MESSAGE}
                    />
                </BoxLoading>
                <Dialog
                    shown={!!notificationIdForRemove}
                    setShown={() => setNotificationIdForRemove(undefined)}
                    wrapperProps={{style: {width: REMOVE_NOTIFICATION_MODAL_WIDTH}}}>
                    {notificationIdForRemove ? (
                        <RemoveNotification
                            notificationId={notificationIdForRemove}
                            onSuccess={onRemoveNotification}
                            onClose={() => setNotificationIdForRemove(undefined)}
                        />
                    ) : null}
                </Dialog>
            </BasicPage>
        </ServerResponseHandler>
    );
};
